import type { OrderRecord } from "@/api/types";
import { orderAction, orderReason } from "@/lib/order-labels";
import { fillPrice } from "@/lib/order-trades";

/** Shared by the order tables (Trades, Live, an exchange's page) and the order details dialog. */

export const num = (v: number | null, digits: number) =>
  v == null || !Number.isFinite(v) || v === 0 ? "—" : v.toLocaleString(undefined, { maximumFractionDigits: digits });

export const usd = (v: number) => `$${v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** Signed dollars: +$12.40, −$3.10. */
export const signedUsd = (v: number) => `${v > 0 ? "+" : v < 0 ? "−" : ""}${usd(Math.abs(v))}`;

export const pnlColor = (v: number | null | undefined) =>
  v == null || v === 0 ? "var(--text-muted)" : v > 0 ? "var(--success)" : "var(--danger)";

/** A rounded quantity for tables: whole units from 100 up, 2 decimals from 1, else 4 significant digits. */
export const approxQty = (v: number) =>
  !v || !Number.isFinite(v)
    ? "—"
    : Math.abs(v) >= 100
      ? v.toLocaleString(undefined, { maximumFractionDigits: 0 })
      : Math.abs(v) >= 1
        ? v.toLocaleString(undefined, { maximumFractionDigits: 2 })
        : v.toLocaleString(undefined, { maximumSignificantDigits: 4 });

export const qtyOf = (o: OrderRecord) => Number(o.executed_qty) || Number(o.requested_qty) || 0;

/** What the order moved in quote currency: reported by the exchange, else price × quantity. */
export const valueOf = (o: OrderRecord) => Number(o.quote_spent) || (fillPrice(o) ?? 0) * (Number(o.executed_qty) || 0);

/** What an order did, in a word or two: why a position closed (Take profit, Stop loss), else "Open short", "Close long"… */
export function orderLabel(o: OrderRecord): string {
  const reason = orderReason(o.comment);
  return reason === "Take profit" || reason === "Stop loss" ? reason : orderAction(o).label;
}
